import client from 'api/apollo';
import { EditYouthAthleteMutation } from 'mutations/EditYouthAthlete';
import { AddYouthAthletes } from 'mutations/AddYouthAthletes';
import { getFromState, omitKeys, pickKeys, logError } from 'helpers';

// Mirrors `input EditYouthAthleteFields` in the API schema
// (wcra-platform-api/src/api/schema/youthAthlete/youthAthlete.graphql).
//
// The edit form is initialised with the whole youth athlete record, so it
// submits every field the profile query returns. Any field not declared on
// EditYouthAthleteFields makes GraphQL reject the entire mutation before it
// reaches the resolver, so the payload is built by naming what the schema
// accepts rather than by listing what to drop - a drop-list silently goes
// stale each time the query behind the form grows a field.
//
// Keep in step with the schema. ERAUID is required there.
const EDITABLE_YOUTH_FIELDS = [
  'ERAUID',
  'firstName',
  'lastName',
  'disciplineId',
  'nickname',
  'hometown',
  'careerEarnings',
  'worldChampionshipEarned',
  'DOB',
  'Gender',
  'height',
  'weight',
  'facebook',
  'twitter',
  'instagram',
  'street',
  'street2',
  'city',
  'state',
  'zip',
  'phone',
  'LegalFirstName',
  'LegalLastName',
  'socialTags',
  'updatedAt',
];

export const YOUTHATHLETE_ASYNC_START = 'YOUTHATHLETE_ASYNC_START';
export const YOUTHATHLETE_ASYNC_FAIL = 'YOUTHATHLETE_ASYNC_FAIL';
export const YOUTHATHLETE_ASYNC_SUCCESS = 'YOUTHATHLETE_ASYNC_SUCCESS';
export const YOUTHATHLETE_ASYNC_CLEAR_ERROR = 'YOUTHATHLETE_ASYNC_CLEAR_ERROR';

export function youthAthleteAsyncStart() {
  return {
    type: YOUTHATHLETE_ASYNC_START,
  };
}

export function youthAthleteAsyncFail(errorMessage) {
  return {
    type: YOUTHATHLETE_ASYNC_FAIL,
    errorMessage,
  };
}

export function youthAthleteAsyncSuccess(data) {
  return {
    type: YOUTHATHLETE_ASYNC_SUCCESS,
    data,
  };
}

export function youthAthleteAsyncClearError() {
  return {
    type: YOUTHATHLETE_ASYNC_CLEAR_ERROR,
  };
}

export function getYouthAthlete(query, id) {
  return async (dispatch) => {
    dispatch(youthAthleteAsyncStart());
    try {
      const queryPayload = {
        query,
        variables: { id },
        fetchPolicy: 'network-only',
      };
      const response = await client
        .query(queryPayload)
        .then(({ data }) => data.youthAthlete);
      dispatch(youthAthleteAsyncSuccess(response));
      return response;
    } catch (error) {
      logError(error);
      dispatch(youthAthleteAsyncFail('Youth athlete not found'));
    }
  };
}

export function editYouthAthlete(values) {
  return async (dispatch, getState) => {
    try {
      const youthAthleteFields = pickKeys(values, EDITABLE_YOUTH_FIELDS);
      const user = getFromState(getState, 'user');
      const input = {
        userId: user.id,
        youthAthlete: youthAthleteFields,
      };
      dispatch(youthAthleteAsyncStart());
      const mutationPayload = {
        mutation: EditYouthAthleteMutation,
        variables: { input },
      };
      const response = await client
        .mutate(mutationPayload)
        .then(({ data }) => data.editYouthAthlete);

      // upsertYouthAthlete reports a failure in the payload rather than by
      // throwing, so a save can come back 200 with success: false and a null
      // youthAthlete. Treating that as a success left the form looking as
      // though it had saved.
      if (!response || !response.success || !response.youthAthlete) {
        const message =
          (response && response.error) || 'Error saving youth athlete';
        dispatch(youthAthleteAsyncFail(message));
        return null;
      }

      dispatch(youthAthleteAsyncSuccess(response.youthAthlete));
      return response;
    } catch (error) {
      logError(error);
      dispatch(youthAthleteAsyncFail('Error saving youth athlete'));
    }
  };
}

export function addYouthAthletes(youthAthletes) {
  return async (dispatch, getState) => {
    try {
      const youthAthleteFields = youthAthletes.map((ya) =>
        omitKeys(ya, ['id', 'country', 'SSN', 'USCitizen']),
      );
      const user = getFromState(getState, 'user');
      const input = {
        userId: user.id,
        youthAthletes: youthAthleteFields,
      };
      const mutationPayload = {
        mutation: AddYouthAthletes,
        variables: { input },
      };
      dispatch(youthAthleteAsyncStart());
      const response = await client
        .mutate(mutationPayload)
        .then(({ data }) => data);
      dispatch(youthAthleteAsyncSuccess({}));
      return response.addYouthAthletes;
    } catch (error) {
      logError(error);
      dispatch(youthAthleteAsyncFail('Error adding youth athlete'));
    }
  };
}
